/**
 * WebSocket Service for handling real-time notifications
 * 
 * This service manages the WebSocket connection for receiving real-time notifications
 * from the server. It handles connection, reconnection, message parsing, and provides
 * callbacks for new notifications.
 * 
 * The service is designed to be initialized when the user logs in and disconnected
 * when the user logs out.
 */

import { tokenService } from './auth/tokenService';

class WebSocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectTimeout = null;
    this.reconnectInterval = 3000; // Start with 3 seconds
    this.listeners = [];
    this.userId = null;
  }

  /**
   * Initialize the WebSocket connection using the current authentication token
   * @param {string} userId - User ID for the WebSocket connection (optional)
   * @returns {Promise} - Resolves when connection is established or rejects on error
   */
  initialize(userId = null) {
    const token = tokenService.getAccessToken();
    if (!token) {
      return Promise.reject(new Error('No authentication token available'));
    }

    // If userId is not provided, try to extract it from stored user data
    // For now, we'll use the default '2' if not provided
    this.userId = userId || tokenService.getUserId();

    return this.connect(token, this.userId);
  }

  /**
   * Connect to the WebSocket server
   * @param {string} token - Authentication token for WebSocket
   * @param {string} userId - User ID for the WebSocket connection
   * @returns {Promise} - Resolves when connection is established or rejects on error
   * @private - This method is intended to be called by initialize(), not directly
   */
  connect(token, userId) {
    return new Promise((resolve, reject) => {
      if (this.socket && this.isConnected) {
        console.log('WebSocket is already connected');
        resolve();
        return;
      }

      try {
        const wsUrl = `wss://api-barterflip.canteradigital.io/api/v1/notifications/ws/user/${userId}?token=${token}`;
        console.log('WebSocket URL:', wsUrl);
        this.socket = new WebSocket(wsUrl);

        this.socket.onopen = () => {
          console.log('WebSocket connection established');
          this.isConnected = true;
          this.reconnectAttempts = 0;
          resolve();
        };

        this.socket.onmessage = (event) => {
          console.log('WebSocket message received:', event.data);
          try {
            const data = JSON.parse(event.data);
            console.log('Parsed WebSocket data:', data);
            this._notifyListeners(data);
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
            // Intentar manejar el mensaje aunque no sea JSON válido
            if (typeof event.data === 'string') {
              console.log('Trying to handle as raw string message');
              this._notifyListeners({
                id: Date.now(),
                title: 'Nueva notificación',
                message: event.data,
                timestamp: new Date().toISOString()
              });
            }
          }
        };

        this.socket.onclose = (event) => {
          this.isConnected = false;
          console.log(`WebSocket connection closed: ${event.code} ${event.reason}`);
          this._attemptReconnect();
        };

        this.socket.onerror = (error) => {
          console.error('WebSocket error:', error);
          reject(error);
        };
      } catch (error) {
        console.error('Error creating WebSocket:', error);
        reject(error);
      }
    });
  }

  /**
   * Attempt to reconnect to the WebSocket server
   * @private
   */
  _attemptReconnect() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('Maximum reconnection attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectInterval * Math.pow(1.5, this.reconnectAttempts - 1);
    console.log(`Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`);

    this.reconnectTimeout = setTimeout(() => {
      // Get a fresh token in case the old one expired
      const token = tokenService.getAccessToken();
      if (token) {
        this.connect(token, this.userId)
          .catch(error => {
            console.error('Reconnection failed:', error);
          });
      } else {
        console.error('Cannot reconnect: No authentication token available');
      }
    }, delay);
  }

  /**
   * Disconnect the WebSocket connection
   */
  disconnect() {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
      this.isConnected = false;
      
      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout);
        this.reconnectTimeout = null;
      }
      
      console.log('WebSocket disconnected');
    }
  }

  /**
   * Add a listener for new notifications
   * @param {Function} callback - Function to call when a new notification is received
   * @returns {number} - Listener ID for removal
   */
  addNotificationListener(callback) {
    console.log('Adding notification listener');
    this.listeners.push(callback);
    return this.listeners.length - 1;
  }

  /**
   * Remove a notification listener
   * @param {number} listenerId - ID of the listener to remove
   */
  removeNotificationListener(listenerId) {
    if (listenerId >= 0 && listenerId < this.listeners.length) {
      this.listeners[listenerId] = null;
    }
  }

  /**
   * Notify all listeners about a new notification
   * @param {Object} data - Notification data
   * @private
   */
  _notifyListeners(data) {
    console.log('Notifying listeners with data:', data);
    console.log('Number of active listeners:', this.listeners.filter(l => l !== null).length);
    
    this.listeners.forEach((listener, index) => {
      if (listener) {
        console.log(`Calling listener ${index}...`);
        try {
          listener(data);
          console.log(`Listener ${index} called successfully`);
        } catch (error) {
          console.error(`Error in listener ${index}:`, error);
        }
      }
    });
  }
}

// Create a singleton instance
const websocketService = new WebSocketService();

export default websocketService;
